/**
 * Install Prompt Component - Floating banner for PWA installation
 */

import { useCallback } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import './InstallPrompt.css';

interface InstallPromptProps {
  onInstall?: () => void;
  onDismiss?: () => void;
  showDelay?: number; // Delay in ms before showing the prompt
}

export function InstallPrompt({
  onInstall,
  onDismiss,
  showDelay = 3000, // Default 3 second delay
}: InstallPromptProps) {
  const { isInstalling, install, dismiss, shouldShowPrompt } = usePWAInstall();

  const handleInstall = useCallback(async () => {
    const success = await install();
    if (success) {
      onInstall?.();
    }
  }, [install, onInstall]);

  const handleDismiss = useCallback(() => {
    dismiss();
    onDismiss?.();
  }, [dismiss, onDismiss]);

  // Don't render if prompt shouldn't be shown
  if (!shouldShowPrompt()) {
    return null;
  }

  return (
    <div className="install-prompt" style={{ animationDelay: `${showDelay}ms` }}>
      <div className="install-prompt-content">
        <div className="install-prompt-icon">📱</div>
        <div className="install-prompt-text">
          <div className="install-prompt-title">Install Refinevoice</div>
          <div className="install-prompt-description">
            Get the full app experience with offline access and quicker launches
          </div>
        </div>
        <div className="install-prompt-actions">
          <button
            type="button"
            className="install-prompt-button install-prompt-dismiss"
            onClick={handleDismiss}
            disabled={isInstalling}
          >
            Not now
          </button>
          <button
            type="button"
            className="install-prompt-button install-prompt-install"
            onClick={handleInstall}
            disabled={isInstalling}
          >
            {isInstalling ? 'Installing...' : 'Install'}
          </button>
        </div>
      </div>
    </div>
  );
}
